import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401) {
        console.error("[Auth] Échec de vérification de session:", err); // eslint-disable-line no-console
        toast.error("Impossible de vérifier votre session. Vérifiez votre connexion.");
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data); return data;
  }, [setUser]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data); return data;
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("[Auth] Logout serveur échoué, déconnexion locale forcée:", err); // eslint-disable-line no-console
    }
    setUser(false);
  }, [setUser]);

  // Dual-role — bascule de la casquette active
  const switchRole = useCallback(async (role) => {
    const { data } = await api.post("/auth/switch-role", { role });
    // Rafraîchit /auth/me pour synchroniser tous les champs (raison_sociale, etc.)
    await refresh();
    return data;
  }, [refresh]);

  // Dual-role — activation de la seconde casquette (fournit les infos manquantes)
  const enableRole = useCallback(async (payload) => {
    const { data } = await api.post("/auth/enable-role", payload);
    await refresh();
    return data;
  }, [refresh]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser, switchRole, enableRole }}>
      {children}
    </AuthCtx.Provider>
  );
}
